import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { ProjectsManager } from '@/components/admin/ProjectsManager';
import { AddProjectForm } from '@/components/admin/AddProjectForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, FolderOpen, ChevronDown, BarChart3 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const AdminProjects = () => {
  const { user, loading } = useAuth();
  const [activeView, setActiveView] = useState<'list' | 'add'>('list');
  const [statsOpen, setStatsOpen] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    images: 0
  });

  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      // Get projects stats
      const { data: projects } = await supabase
        .from('projects')
        .select('status');
      
      const { data: images } = await supabase
        .from('project_images')
        .select('id');

      const total = projects?.length || 0;
      const published = projects?.filter(p => p.status === 'published').length || 0;
      const draft = projects?.filter(p => p.status === 'draft').length || 0;
      const imagesCount = images?.length || 0;

      setStats({ total, published, draft, images: imagesCount });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <>
      <SEOHead 
        title="Admin - Projects Management"
        description="Manage roofing projects and gallery"
      />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold">Projects Management</h1>
                <p className="text-sm text-muted-foreground">Manage roofing projects and gallery images</p>
              </div>
              <Button 
                onClick={() => {
                  setActiveView(activeView === 'add' ? 'list' : 'add');
                  if (activeView === 'list') setStatsOpen(false);
                }}
                className="w-full sm:w-auto min-h-[44px]"
              >
                {activeView === 'add' ? (
                  <>
                    <FolderOpen className="h-4 w-4" />
                    View Projects
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Add Project
                  </>
                )}
              </Button>
            </div>

            {/* Compact Stats Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                <BarChart3 className="h-3 w-3 mr-1" />
                {stats.total} Total
              </Badge>
              <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
                {stats.published} Published
              </Badge>
              <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20">
                {stats.draft} Drafts
              </Badge>
              <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20">
                {stats.images} Images
              </Badge>
            </div>
          </div>

          {/* Collapsible Stats Cards */}
          <Collapsible open={statsOpen} onOpenChange={setStatsOpen}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full justify-between min-h-[44px] touch-manipulation"
              >
                <span className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Detailed Statistics
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${statsOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Published</CardTitle>
                    <div className="h-2 w-2 bg-green-500 rounded-full" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.published}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Drafts</CardTitle>
                    <div className="h-2 w-2 bg-yellow-500 rounded-full" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.draft}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Images</CardTitle>
                    <div className="h-2 w-2 bg-blue-500 rounded-full" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.images}</div>
                  </CardContent>
                </Card>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Content */}
          <div className="space-y-6">
            {activeView === 'add' ? (
              <AddProjectForm />
            ) : (
              <ProjectsManager />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminProjects;